import React, { useState } from "react";
import PropTypes from "prop-types";
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Radio,
  alpha,
} from "@mui/material";
import { Icon } from "@iconify/react";

const sourceTypes = [
  {
    id: "csv",
    icon: "mdi:file-delimited",
    title: "CSV / Excel",
    description:
      "Importieren Sie Daten aus CSV-Dateien oder Excel-Exporten. Ideal für einfache Mitgliederlisten.",
    features: [
      "Unterstützt Semikolon- und Komma-Trennung",
      "Automatische Zeichenerkennung",
      "Flexible Spaltenzuordnung",
    ],
    color: "#2e7d32",
  },
  {
    id: "json",
    icon: "mdi:code-json",
    title: "Legacy JSON",
    description:
      "Importieren Sie strukturierte JSON-Daten aus Ihrem alten System. Enthält bereits Standorte, Verträge und mehr.",
    features: [
      "Vollständige Datenstruktur",
      "Automatische Zuordnung",
      "Alle Entitäten in einer Datei",
    ],
    color: "#1976d2",
  },
  {
    id: "manual",
    icon: "mdi:keyboard",
    title: "Manuelle Eingabe",
    description:
      "Erstellen Sie Ihre ersten Mitglieder und Standorte manuell im System. Empfohlen für kleine Schulen.",
    features: [
      "Schritt-für-Schritt Eingabe",
      "Keine Dateivorbereitung nötig",
      "Perfekt für den Start",
    ],
    color: "#ed6c02",
  },
];

function SelectSourceType({ selectedType, onSelect }) {
  const [selected, setSelected] = useState(selectedType);

  const handleSelect = (typeId) => {
    setSelected(typeId);
    onSelect(typeId);
  };

  return (
    <Box>
      <Typography variant="h5" gutterBottom fontWeight="bold" textAlign="center">
        Wählen Sie das Format Ihrer Daten
      </Typography>
      <Typography variant="body1" color="text.secondary" textAlign="center" paragraph>
        Welche Art von Daten möchten Sie importieren?
      </Typography>

      <Grid container spacing={3} sx={{ mt: 2 }}>
        {sourceTypes.map((type) => (
          <Grid item xs={12} md={4} key={type.id}>
            <Card
              elevation={selected === type.id ? 8 : 1}
              sx={{
                height: "100%",
                position: "relative",
                border: 2,
                borderColor: selected === type.id ? type.color : "transparent",
                transition: "all 0.3s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 6,
                },
              }}
            >
              <CardActionArea onClick={() => handleSelect(type.id)} sx={{ height: "100%" }}>
                <CardContent sx={{ p: 3, textAlign: "center" }}>
                  <Box
                    sx={{
                      position: "absolute",
                      top: 16,
                      right: 16,
                    }}
                  >
                    <Radio checked={selected === type.id} color="primary" />
                  </Box>

                  <Box
                    sx={{
                      width: 80,
                      height: 80,
                      borderRadius: "50%",
                      bgcolor: alpha(type.color, 0.1),
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      mx: "auto",
                      mb: 2,
                    }}
                  >
                    <Icon icon={type.icon} width={48} style={{ color: type.color }} />
                  </Box>

                  <Typography variant="h6" gutterBottom fontWeight="bold">
                    {type.title}
                  </Typography>

                  <Typography variant="body2" color="text.secondary" paragraph>
                    {type.description}
                  </Typography>

                  <Box sx={{ mt: 2, textAlign: "left" }}>
                    {type.features.map((feature, index) => (
                      <Box key={index} sx={{ display: "flex", alignItems: "center", gap: 1, mb: 1 }}>
                        <Icon icon="mdi:check-circle" width={16} style={{ color: type.color }} />
                        <Typography variant="caption">{feature}</Typography>
                      </Box>
                    ))}
                  </Box>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
}

SelectSourceType.propTypes = {
  selectedType: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
};

export default SelectSourceType;
